<template>
  <div class="searchable-select" v-click-outside="close">
    <button
      type="button"
      class="form-control form-control-sm searchable-select-trigger"
      :class="[stateClass, { 'searchable-select-trigger--open': open }]"
      :disabled="disabled"
      @click="toggle"
    >
      <span :class="{ 'searchable-select-placeholder': !selectedLabel }">{{
        selectedLabel || placeholder
      }}</span>
    </button>
    <div v-if="open" class="searchable-select-menu">
      <input
        ref="search"
        v-model="search"
        type="text"
        class="form-control form-control-sm searchable-select-search"
        placeholder="Buscar..."
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc.prevent="close"
      />
      <ul class="searchable-select-list">
        <li
          v-if="clearItem"
          class="searchable-select-option searchable-select-option--clear"
          :class="{ 'searchable-select-option--selected': value === null || value === undefined }"
          @click="select(clearItem)"
        >
          {{ clearItem.text }}
        </li>
        <li
          v-if="pinnedItem"
          class="searchable-select-option searchable-select-option--pinned"
          :class="{ 'searchable-select-option--selected': pinnedItem.value === value }"
          @click="select(pinnedItem)"
        >
          {{ pinnedItem.text }}
        </li>
        <li v-if="!flatOptions.length" class="searchable-select-empty">Nenhum resultado.</li>
        <template v-for="group in filteredGroups">
          <li v-if="group.label" :key="`label-${group.label}`" class="searchable-select-group-label">
            {{ group.label }}
          </li>
          <li
            v-for="opt in group.items"
            :key="`${group.label || ''}-${opt.value}`"
            :class="[
              'searchable-select-option',
              {
                'searchable-select-option--selected': opt.value === value,
                'searchable-select-option--highlighted': opt.flatIndex === highlightedIndex,
              },
            ]"
            @click="select(opt)"
            @mousemove="highlightedIndex = opt.flatIndex"
          >
            {{ opt.text }}
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
// Substitui o <select> nativo (via b-form-select) por uma lista filtrável por
// digitação — usado onde a lista de opções vem de um cadastro (sensores,
// hidrômetros, atuadores, bombas...) e pode chegar a dezenas/centenas de itens,
// o que torna o <select> nativo (sem busca) impraticável de navegar.
export default {
  name: "searchable-select",
  directives: {
    "click-outside": {
      bind(el, binding) {
        el.__clickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el.__clickOutsideHandler__, true);
      },
      unbind(el) {
        document.removeEventListener("click", el.__clickOutsideHandler__, true);
      },
    },
  },
  props: {
    value: { default: null },
    // Caso simples (maioria dos usos): uma lista única de objetos.
    options: { type: Array, default: () => [] },
    textField: { type: String, default: "text" },
    valueField: { type: String, default: "value" },
    // Caso com múltiplas origens agrupadas (ex.: "Válvulas" + "Bombas" no mesmo
    // select) — cada grupo pode ter seu próprio textField/valueField/prefix.
    // Ignorado quando não informado; `options` é usado nesse caso.
    groups: { type: Array, default: null },
    placeholder: { type: String, default: "Selecione" },
    // Fallback do padrão "{{ NOME }} (atual)" usado nos formulários de edição
    // quando o item associado no momento não está mais na lista de disponíveis
    // (ex.: get-alones-sensor-*-list só devolve os NÃO associados a nenhum DIR).
    currentValue: { default: null },
    currentLabel: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // Mesma semântica do :state do b-form-select/BootstrapVue: true/"valid" -> is-valid,
    // false/"invalid" -> is-invalid, null/undefined -> sem classe.
    state: { type: [Boolean, String], default: null },
    // O <option :value="null"> do b-form-select nativo era selecionável por padrão
    // (permitia desassociar), só ficava indisponível quando marcado "disabled" (campos
    // obrigatórios, ex. Modelo). Aqui o padrão segue o mesmo: :clearable="false" só nos
    // campos obrigatórios que antes usavam a option disabled.
    clearable: { type: Boolean, default: true },
  },
  data() {
    return {
      open: false,
      search: "",
      highlightedIndex: -1,
    };
  },
  computed: {
    normalizedGroups() {
      if (this.groups) {
        return this.groups.map((g) => ({
          label: g.label || null,
          items: (g.items || []).map((item) => ({
            value: g.prefix ? `${g.prefix}${item[g.valueField || this.valueField]}` : item[g.valueField || this.valueField],
            text: item[g.textField || this.textField],
          })),
        }));
      }
      return [
        {
          label: null,
          items: this.options.map((item) => ({
            value: item[this.valueField],
            text: item[this.textField],
          })),
        },
      ];
    },
    // Lista plana (sem grupo) só pra indexar navegação por teclado/checar "sem resultado".
    flatOptions() {
      return this.normalizedGroups.flatMap((g) => g.items);
    },
    // Opção fixa do padrão "{{ NOME }} (atual)" — sempre visível no topo, imune ao
    // filtro de busca, e omitida se o valor atual já estiver na lista normal.
    pinnedItem() {
      if (this.currentValue === null || this.currentValue === undefined || !this.currentLabel) return null;
      if (this.flatOptions.some((opt) => opt.value === this.currentValue)) return null;
      return { value: this.currentValue, text: `${this.currentLabel} (atual)` };
    },
    // Opção fixa pra limpar a seleção (equivalente ao <option :value="null"> do
    // <select> nativo) — sempre visível no topo, imune ao filtro de busca.
    clearItem() {
      if (!this.clearable) return null;
      return { value: null, text: this.placeholder };
    },
    filteredGroups() {
      const term = this.search.trim().toLowerCase();
      let flatIndex = 0;
      return this.normalizedGroups
        .map((g) => {
          const items = g.items
            .filter((opt) => !term || String(opt.text).toLowerCase().includes(term))
            .map((opt) => ({ ...opt, flatIndex: flatIndex++ }));
          return { label: g.label, items };
        })
        .filter((g) => g.items.length);
    },
    stateClass() {
      if (this.state === true || this.state === "valid") return "is-valid";
      if (this.state === false || this.state === "invalid") return "is-invalid";
      return "";
    },
    selectedLabel() {
      if (this.value === null || this.value === undefined) return null;
      const found = this.flatOptions.find((opt) => opt.value === this.value);
      if (found) return found.text;
      if (this.currentValue !== null && this.value === this.currentValue && this.currentLabel) {
        return `${this.currentLabel} (atual)`;
      }
      return null;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.search = "";
        this.highlightedIndex = -1;
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      }
    },
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
    },
    close() {
      this.open = false;
    },
    select(opt) {
      this.$emit("input", opt.value);
      this.$emit("change", opt.value);
      this.close();
    },
    move(delta) {
      const total = this.filteredGroups.reduce((n, g) => n + g.items.length, 0);
      if (!total) return;
      let next = this.highlightedIndex + delta;
      if (next < 0) next = total - 1;
      if (next >= total) next = 0;
      this.highlightedIndex = next;
    },
    selectHighlighted() {
      const target = this.filteredGroups.flatMap((g) => g.items).find((o) => o.flatIndex === this.highlightedIndex);
      if (target) this.select(target);
      else if (this.flatOptions.length === 1) this.select({ ...this.flatOptions[0], flatIndex: 0 });
    },
  },
};
</script>

<style scoped>
.searchable-select {
  position: relative;
}
.searchable-select-trigger {
  width: 100%;
  text-align: left;
  background-color: #fff;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.searchable-select-trigger:after {
  content: "";
  margin-left: auto;
  border: solid #6c757d;
  border-width: 0 2px 2px 0;
  padding: 3px;
  transform: rotate(45deg);
  flex-shrink: 0;
}
.searchable-select-trigger--open:after {
  transform: rotate(-135deg);
}
.searchable-select-trigger:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}
.searchable-select-placeholder {
  color: #6c757d;
}
.searchable-select-menu {
  position: absolute;
  z-index: 1000;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 2px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
.searchable-select-search {
  border-radius: 0;
  border-width: 0 0 1px 0;
}
.searchable-select-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
}
.searchable-select-group-label {
  padding: 4px 10px;
  font-size: 0.72em;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  background: #f5f6f5;
}
.searchable-select-option {
  padding: 5px 10px;
  font-size: 0.85em;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.searchable-select-option--highlighted {
  background-color: #eef5f0;
}
.searchable-select-option--selected {
  font-weight: 700;
  color: #3d9161;
}
.searchable-select-option--pinned {
  font-style: italic;
  border-bottom: 1px solid #eee;
}
.searchable-select-option--clear {
  color: #6c757d;
  font-style: italic;
  border-bottom: 1px solid #eee;
}
.searchable-select-empty {
  padding: 6px 10px;
  font-size: 0.82em;
  color: #999;
}
</style>
